import Pusher from "pusher-js";
import { track } from "@/lib/analytics/track";
import { EVENTS } from "@/lib/analytics/events";

let pusherClient = null;
let connectionAttempts = 0;
const MAX_ATTEMPTS = 10; // Increase max attempts
let reconnectTimer = null;
const trackedErrors = new Map();
const NOOP_CHANNEL = {
  bind: () => {},
  unbind: () => {},
  unbind_all: () => {},
  subscribed: false,
};

function isIgnorableTransportError(error) {
  const message = `${error?.message || ""} ${error?.error || ""}`.toLowerCase();
  const name = (error?.name || "").toLowerCase();
  const type = (error?.type || "").toLowerCase();

  return (
    message.includes("socket is not connected") ||
    name.includes("transportconnectionerror") ||
    type.includes("transportconnectionerror")
  );
}

function trackPusherError(error, context = {}) {
  try {
    const key = [
      context.event || "unknown",
      error?.type || "",
      error?.name || "",
      error?.message || error?.error || "",
    ].join(":");
    const now = Date.now();
    const prev = trackedErrors.get(key);

    // Deduplicate repeated transport noise bursts for 30 seconds.
    if (prev && now - prev < 30000) return;
    trackedErrors.set(key, now);

    track(EVENTS.CLIENT_ERROR, {
      source: "pusher.connection",
      event: context.event,
      error_type: error?.type || undefined,
      error_name: error?.name || "Error",
      error_message: error?.message || error?.error || "Unknown error",
      error_code: error?.data?.code || error?.code || undefined,
      connection_state: pusherClient?.connection?.state || undefined,
      socket_id_present: Boolean(pusherClient?.connection?.socket_id),
      is_ignorable_transport: isIgnorableTransportError(error),
    });
  } catch {
    // Analytics must never interrupt app behavior.
  }
}

function patchClientMethods(client) {
  const rawSubscribe = client.subscribe.bind(client);
  const rawUnsubscribe = client.unsubscribe.bind(client);

  client.subscribe = function patchedSubscribe(channelName) {
    try {
      return rawSubscribe(channelName);
    } catch (error) {
      trackPusherError(error, { event: "subscribe" });

      if (isIgnorableTransportError(error)) {
        scheduleReconnect();
        return client.channel(channelName) || NOOP_CHANNEL;
      }

      throw error;
    }
  };

  client.unsubscribe = function patchedUnsubscribe(channelName) {
    try {
      return rawUnsubscribe(channelName);
    } catch (error) {
      trackPusherError(error, { event: "unsubscribe" });

      if (isIgnorableTransportError(error)) {
        scheduleReconnect();
        return;
      }

      throw error;
    }
  };
}

/**
 * Get or create a singleton instance of the Pusher client
 * Only initializes in browser environment
 * @returns {Pusher|null} The Pusher client instance or null if in server environment
 */
export function getPusherClient() {
  // Only create client in browser environment
  if (typeof window === "undefined") return null;

  if (!pusherClient) {
    // Enhanced configuration for better connection handling
    pusherClient = new Pusher(process.env.NEXT_PUBLIC_PUSHER_KEY, {
      cluster: process.env.NEXT_PUBLIC_PUSHER_CLUSTER,
      authEndpoint: "/api/pusher/auth",
      auth: {
        credentials: "include",
      },
      // Improved transport configuration
      enabledTransports: ["ws", "wss"],
      disabledTransports: [],
      timeout: 25000, // Increase connection timeout
      pongTimeout: 20000, // Increase pong timeout
      activityTimeout: 300000, // Increase to 5 minutes
      autoReconnect: true,
      maxReconnectionAttempts: 10,
      // Add forced TLS for better reliability
      forceTLS: true,
    });

    patchClientMethods(pusherClient);

    // Enhanced event listeners with better error handling
    pusherClient.connection.bind("connected", () => {
      connectionAttempts = 0;

      // Clear any existing reconnect timer
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    });

    pusherClient.connection.bind("disconnected", () => {
      console.warn("Pusher disconnected, will attempt reconnect");
    });

    pusherClient.connection.bind("error", (err) => {
      trackPusherError(err, { event: "connection_error" });

      // Handle different error types
      if (err.type === "WebSocketError" || err.type === "PusherError") {
        scheduleReconnect();
      }
    });

    pusherClient.connection.bind("failed", () => {
      trackPusherError(new Error("Pusher connection failed"), {
        event: "connection_failed",
      });
      scheduleReconnect();
    });

    // Handle page visibility changes for better reconnection
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", () => {
        if (
          document.visibilityState === "visible" &&
          pusherClient.connection.state === "disconnected"
        ) {
          scheduleReconnect();
        }
      });
    }
  }

  return pusherClient;
}

/**
 * Schedule a reconnection attempt with exponential backoff
 */
function scheduleReconnect() {
  if (reconnectTimer || connectionAttempts >= MAX_ATTEMPTS) {
    return;
  }

  const delay = Math.min(1000 * Math.pow(2, connectionAttempts), 30000);
  connectionAttempts++;

  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;

    if (pusherClient && pusherClient.connection.state !== "connected") {
      try {
        pusherClient.connect();
      } catch (error) {
        // console.error("Error during Pusher reconnect:", error);
        scheduleReconnect();
      }
    }
  }, delay);
}

/**
 * Subscribe to a user's private channel
 * @param {string} userId - The user ID to subscribe for
 * @returns {PusherChannel|null} The channel object or null if couldn't subscribe
 */
export function subscribeToUserChannel(userId) {
  try {
    const client = getPusherClient();
    if (!client || !userId) return null;

    const channelName = `private-user-${userId}`;

    // Check if already subscribed
    const existingChannel = client.channel(channelName);
    if (existingChannel && existingChannel.subscribed) {
      return existingChannel;
    }

    const channel = client.subscribe(channelName);

    // Enhanced error handling for channel subscription
    channel.bind("pusher:subscription_error", (error) => {
      // console.error(`Subscription error for channel ${channelName}:`, error);

      // Retry subscription after a delay
      setTimeout(() => {
        if (client.connection.state === "connected") {
          client.subscribe(channelName);
        }
      }, 5000);
    });

    channel.bind("pusher:subscription_succeeded", () => {
      // console.log(`Successfully subscribed to ${channelName}`);
    });

    return channel;
  } catch (error) {
    // console.error("Failed to subscribe to user channel:", error);
    return null;
  }
}

/**
 * Safely unsubscribe from a channel
 * @param {string} channelName - The channel name to unsubscribe from
 */
export function safelyUnsubscribe(channelName) {
  try {
    const client = getPusherClient();
    if (!client) return;

    const channel = client.channel(channelName);
    if (channel) {
      channel.unbind_all();
      client.unsubscribe(channelName);
    }
  } catch (error) {
    // console.error(`Error unsubscribing from ${channelName}:`, error);
  }
}

/**
 * Force reconnect Pusher connection
 * Useful for manual reconnection triggers
 */
export function forceReconnect() {
  if (pusherClient) {
    connectionAttempts = 0;

    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }

    try {
      pusherClient.disconnect();
      setTimeout(() => {
        pusherClient.connect();
      }, 1000);
    } catch (error) {
      // console.error("Error during forced reconnect:", error);
    }
  }
}

/**
 * Get current connection state
 */
export function getConnectionState() {
  return pusherClient?.connection?.state || "unknown";
}
